import { useEffect, useRef, useState } from "react";

import { useMutation } from "@apollo/client";
import Grid from "@mui/material/Grid";
import SwipeableDrawer from '@mui/material/SwipeableDrawer';

import { INSERT_MEAL, INSERT_MEAL_PLAN, INSERT_SHOPPING_LIST_FOR_USER, INSERT_USER_TO_MEAL_PLAN } from "../../../app/graphql/mutations";
import { setGroceryListId } from "../../../app/groceryList/groceryList.slice";
import { mealRecipe } from "../../../app/homepage/homepage.types";
import { userSelector } from "../../../app/main/mainSlice";
import { SelectedDateSelector, setSelectedDate, UserMealPlanDataSelector } from "../../../app/mealPlan/mealPlan.slice";
import { useAppDispatch, useAppSelector } from "../../../app/store/hooks";
import useUpdateGroceryList from "../../../hooks/groceryList/useUpdateGroceryList";
import useFetchGroceryListId from "../../../hooks/useFetchGroceryListId";
import useFetchMealPlan from "../../../hooks/useFetchMealPlan";
import useLogRecipeEdit from "../../../hooks/useLogRecipeEdit";
import useTimeoutHandler from "../../../hooks/useTimeoutHandler";
import RecipeMealPlanLoader from "../../../pages/mealPlan/RecipeMealPlanLoader";
import Calendar from "../../mealPlanPage/Calendar/Calendar";
import { MEAL_SCHEDULE } from "../../mealPlanPage/constants";
import { capitalizeMealType, filterMealsByDate, getAdjustedDateTime, getMealStatus, getMealTime, MEAL_STATUSES, transformMeals } from "../../mealPlanPage/utils";
import PlannedMealsForToday from "./plannedMealsForToday/PlannedMealsForToday";
import ScheduleButton from "./scheduleButton/ScheduleButton";
import styles from "./scheduleMealDrawer.module.css"
import ScheduleMealHeading from "./scheduleMealHeading/ScheduleMealHeading";
import ScheduleMealLoader from "./scheduleMealLoader/ScheduleMealLoader";
import ScheduleMealTimings from "./scheduleMealTimings/ScheduleMealTimings";

const mealTypes = ["Morning", "Afternoon", "Evening", "Night"];

const getCurrentMealType = (selectedDate: Date): string => {
  // Find the first meal in MEAL_SCHEDULE with the status of CURRENT
  const currentMeal = MEAL_SCHEDULE.find(meal => getMealStatus(meal.mealType, selectedDate) === MEAL_STATUSES.CURRENT);
  return currentMeal ? currentMeal.mealType : '';
};

const ScheduleMealDrawer = ({ drawer, setDrawer, recipe }: { drawer: boolean, setDrawer: React.Dispatch<React.SetStateAction<boolean>>, recipe: mealRecipe }) => {
  const adjustedDate = getAdjustedDateTime(); // gives next date if current time is past the last meal time for the day
  const [currentDate, setCurrentDate] = useState<Date>(adjustedDate);
  const selectedDate = useAppSelector(SelectedDateSelector);
  const [addingMealToDB, setAddingMealToDB] = useState<boolean>(false);
  const [activeMealType, setActiveMealType] = useState<string>(getCurrentMealType(selectedDate));
  const { updateGroceryListFromAddingMeal } = useUpdateGroceryList();
  const servingSize = useAppSelector((state) => state.recipePage.servingSize)
  const [hasCompletedLoop, setHasCompletedLoop] = useState(false);

  const dispatch = useAppDispatch();
  const user = useAppSelector(userSelector)
  const [addMealPlanDB] = useMutation(INSERT_MEAL_PLAN);
  const [addUserToMealPlanDB] = useMutation(INSERT_USER_TO_MEAL_PLAN);
  const [addMealDB] = useMutation(INSERT_MEAL);
  const { logRecipeEdit } = useLogRecipeEdit();
  const { loading: mealPlanDataLoading, refetch } = useFetchMealPlan();
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const mealPlanData = useAppSelector(UserMealPlanDataSelector);
  const mealPlanId = mealPlanData?.meal_plan_id;
  const meals = mealPlanData?.Meal_Plan?.Meals;
  const { groceryListId } = useFetchGroceryListId();
  const [addShoppingListForUser] = useMutation(INSERT_SHOPPING_LIST_FOR_USER);

  const selectedDateMeals = filterMealsByDate(selectedDate, meals);
  const transformedMeals = transformMeals(mealTypes, selectedDateMeals);
  const [startTimeout] = useTimeoutHandler(() => {
    setHasCompletedLoop(true);
  });
  const [startLoaderTimer] = useTimeoutHandler(() => {
    setShowLoadingModal(false);
  });

  const addShoppingListId = async () => {
    const data = await addShoppingListForUser({
      variables: {
        user_id: user?.id
      }
    })
    const shoppingListId = data?.data?.insert_User_To_Shopping_List_one?.shopping_list_id;
    if (shoppingListId)
      dispatch(setGroceryListId(shoppingListId))
    else {
      console.error("Error generating shopping list id")
    }

    return shoppingListId;
  }

  const handleModalClose = () => {
    startLoaderTimer(1250);
  }

  const handleScheduleMeal = async () => {
    if (!recipe.id || !selectedDate || !activeMealType) return;

    setDrawer(false);
    setAddingMealToDB(true);
    setShowLoadingModal(true);
    startTimeout(1800);

    let mealPlan;
    let shoppingListId;
    if (!groceryListId) shoppingListId = await addShoppingListId();

    try {
      if (!mealPlanId) {
        mealPlan = await addMealPlanDB({
          variables: {
            title: `${user?.nickName}'s Meal Plan`,
            description: "My first meal plan",
            author: user?.id,
            author_profile_Image: user?.photoURL ?? null
          }
        });

        const mealPlanID = mealPlan?.data?.insert_Meal_Plan_one?.id;

        if (mealPlanID) {
          logRecipeEdit("MEAL_PLAN_CREATED", {
            meal_plan_id: mealPlanID,
          })

          await addUserToMealPlanDB({
            variables: {
              meal_plan_id: mealPlanID,
              user_id: user?.id
            }
          });
        } else {
          throw new Error('Meal plan creation failed');
        }
      }

      const currentMealPlanId = mealPlanId || mealPlan?.data?.insert_Meal_Plan_one?.id;
      const scheduledTime = getMealTime(activeMealType, selectedDate);
      const capitalizedMealType = capitalizeMealType(activeMealType);
      const { data: { insert_Meal: { returning: addedSingleMealReturnData } } } = await addMealDB({
        variables: {
          meal_plan_id: currentMealPlanId,
          recipe_id: recipe?.id,
          scheduled_date_time: scheduledTime,
          meal_type: capitalizedMealType,
          serving_size: servingSize ?? recipe?.metadata?.minServing
        }
      });

      await updateGroceryListFromAddingMeal(addedSingleMealReturnData, shoppingListId);

      logRecipeEdit("ADDED_MEAL", {
        eventData: {
          meals: {
            meal_type: capitalizedMealType,
            meal_plan_id: currentMealPlanId,
            recipe_id: recipe?.id,
            scheduled_date_time: scheduledTime.toISOString(),
            serving_size: servingSize ?? recipe?.metadata?.minServing,
          },
          source: "recipe",
        },
      })
      refetch();
    } catch (error) {
      console.error("Error while adding data:", error);
    } finally {
      handleModalClose();
      setAddingMealToDB(false);
    }
  }
  const onSelectedDateChange = (newSelectedDate: Date) => {
    const now = new Date();
    let newActiveMealType;
    if (newSelectedDate > now) {
      newActiveMealType = MEAL_SCHEDULE[0].mealType;
    }
    else {
      newActiveMealType = getCurrentMealType(newSelectedDate);
    }
    setActiveMealType(newActiveMealType)
  }

  return (
    <SwipeableDrawer
      anchor='bottom'
      className='filterDrawer'
      onClose={() => setDrawer(false)}
      onOpen={() => setDrawer(true)}
      open={drawer}
      swipeAreaWidth={56}
      sx={{
        '& .MuiPaper-root': {
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          maxWidth: '960px',
          margin: 'auto',
        },
      }}
    >
      <Grid className={styles.drawer}>
        <Grid className={styles.drawer_content}>
          <ScheduleMealHeading recipeName={recipe?.metadata?.name} setDrawer={setDrawer} />
          {!mealPlanDataLoading ? <><Grid className={styles.calender_container}>
            <Calendar currentDate={currentDate} disablePastDateSelection meals={mealPlanData?.Meal_Plan?.Meals}
              onSelectedDateChange={onSelectedDateChange} selectedDate={selectedDate} setCurrentDate={setCurrentDate} />
          </Grid>
            <ScheduleMealTimings activeMealType={activeMealType} selectedDate={selectedDate} setActiveMealType={setActiveMealType} />
            <PlannedMealsForToday meals={transformedMeals} recipe={recipe} />
          </> : null}
          <Grid className={styles.loader_wrapper}><ScheduleMealLoader loading={mealPlanDataLoading} /></Grid>
        </Grid>
        {!mealPlanDataLoading ? <ScheduleButton handleScheduleMeal={handleScheduleMeal} /> : null}
        {showLoadingModal ? <RecipeMealPlanLoader hasCompletedLoop={hasCompletedLoop} loading={addingMealToDB} /> : null}
      </Grid>
    </SwipeableDrawer>
  )
}

export default ScheduleMealDrawer